import styled from "styled-components";
import { Button, Box } from "@mui/material";
import { deviceSpec } from "breakpoints";

export const ComplianceContainer = styled.div`
  padding: 1rem;
  margin: 0 1rem 1rem 1rem;
  background-color: ${({ theme }) => theme.colors.tableBackground || "#fff"};
  color: ${({ theme }) => theme.colors.textColor || "#000"};
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

export const FrameworkWrapper = styled.div`
  margin-bottom: 2.5rem;
`;

export const FrameworkSelect = styled.select`
  border: 2px solid ${({ theme }) => theme.colors.light || "#460073"};
  width: 30rem;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  background-color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.tableBackground : theme.colors.selectGrey};
  color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.textColor : theme.colors.selectText};
  margin-top: 0.5rem;
  appearance: none;
  background-image: none; /* Removed - we're using a custom dropdown indicator */
  padding-right: 3rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(70, 0, 115, 0.1), 0 1px 3px rgba(70, 0, 115, 0.05);
  font-weight: 500;
  letter-spacing: 0.01em;

  /* Subtle glow for dark mode */
  @media (prefers-color-scheme: dark) {
    border-color: #A68BFA;
    box-shadow: 0 2px 8px rgba(70, 0, 115, 0.1), 0 0 2px rgba(70, 0, 115, 0.08);
  }

  &:hover {
    border-color: #AF24FF;
    box-shadow: 0 3px 8px rgba(70, 0, 115, 0.12), 0 2px 4px rgba(70, 0, 115, 0.06);
    transform: translateY(-1px);
    
    @media (prefers-color-scheme: dark) {
      box-shadow: 0 3px 10px rgba(70, 0, 115, 0.15), 0 1px 4px rgba(70, 0, 115, 0.1);
    }
  }

  &:focus {
    outline: none;
    border-color: #A68BFA;
    box-shadow: 0 0 0 2px rgba(${({ theme }) => theme.colors.highlight2?.replace('#', '').match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ') || "175, 36, 255"}, 0.15), 0 2px 8px rgba(70, 0, 115, 0.08);
  }

  /* Override for select dropdown arrow in Firefox */
  @-moz-document url-prefix() {
    text-indent: 0.01px;
    text-overflow: '';
    padding-right: 1rem;
  }

  @media ${deviceSpec.isTablet} {
    width: 100%;
  }
`;

export const HeaderControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${deviceSpec.isTablet} {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

export const SectionDivider = styled.div`
  border-bottom: 0.3rem solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.125)"};
  margin-bottom: 2.5rem;
  width: 100%;
`;

export const FilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  color: #FFFFFF;
  background-color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.lightButton : theme.colors.light};
  border: none;
  border-radius: 2rem;
  box-shadow: 0 4px 12px rgba(70, 0, 115, 0.2), 0 2px 4px rgba(70, 0, 115, 0.1);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.165, 0.84, 0.44, 1);
  height: 2.5rem;
  min-width: 6rem;
  letter-spacing: 0.02em;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
    box-shadow: 0 6px 16px rgba(175, 36, 255, 0.25), 0 2px 4px rgba(175, 36, 255, 0.15);
    transform: translateY(-1px);
    
    &::after {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(175, 36, 255, 0.2);
  }
`;

export const ExportButton = styled(FilterButton)`
  background-color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.lightButton : theme.colors.light};
  box-shadow: 0 4px 12px rgba(175, 36, 255, 0.2), 0 2px 4px rgba(175, 36, 255, 0.1);
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.light || "#460073"};
    box-shadow: 0 6px 16px rgba(70, 0, 115, 0.25), 0 2px 4px rgba(70, 0, 115, 0.15);
  }
`;

export const TableContainer = styled.div`
  margin-left: 1rem;
`;

export const ComplianceTableEl = styled.table`
  width: 100%;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  table-layout: fixed;
`;

export const TableHead = styled.thead`
  border-bottom: 4px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.125)"};
`;

export const SectionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.125)"};
  margin-bottom: 1.5rem;
`;

export const SectionTitle2 = styled.h3`
  font-weight: 600;
  text-align: left;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  padding: 0.25rem 0;
  margin-right: 1.5rem;
`;

export const ComplianceBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
`;

export const PassedBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 700;
  justify-content: flex-start;
`;

export const ProgressBar = styled.div`
  width: 8rem;
  height: 0.5rem;
  background-color: ${({ theme }) => theme.colors.lightGrey || "#555555"};
  border-radius: 9999px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ width: string }>`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
  border-radius: 9999px;
  width: ${props => props.width};
`;

export const FindingsTable = styled.table`
  width: 100%;
  text-align: left;
  font-size: 0.875rem;
  border-spacing: 0;
  table-layout: fixed;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
  overflow: visible;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

export const FindingsTableHead = styled.thead`
  background-color: ${({ theme }) => `${theme.colors.tableBackground}f0` || "#ffff"};
  color: ${({ theme }) => theme.colors.textColor || "#000"};
  border-bottom: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.125)"};
  
  th:first-child {
    padding-left: 2rem;
  }
  
  tr {
    background: linear-gradient(to right, ${({ theme }) => theme.colors.light || "#460073"}08, transparent);
  }
`;

export const FindingsTableHeadCell = styled.th`
  padding: 0.75rem 0.5rem;
  font-weight: 600;
  text-align: left;
  letter-spacing: 0.02em;
  font-size: 0.8125rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.textSecondaryColor : "#CFCFCF"};
`;

export const FullDescription = styled.div`
  color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.textSecondaryColor : "#CFCFCF"};
`;

export const FindingsTableRow = styled.tr`
  border-top: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.125)"};
  position: relative;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.tableHoverBackground || "rgba(175, 36, 255, 0.03)"};
  }
  
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.tableStripeBackground || "rgba(0, 0, 0, 0.01)"};
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.tableHoverBackground || "rgba(175, 36, 255, 0.03)"};
    }
  }
`;

export const FindingsTableCell = styled.td`
  padding: 0.875rem 0.5rem;
  vertical-align: middle;
  line-height: 1.5;
  transition: all 0.2s ease;
  border-bottom: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.05)"};
  
  &:last-child {
    border-right: none;
  }
`;

export const SeverityCell = styled(FindingsTableCell)`
  color: ${({ theme }) => theme.colors.critical_criticality || "#F35C88"};
  font-weight: 600;
`;

export const ViewLink = styled.button`
  color: ${({ theme }) => theme.colors.white };
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  box-shadow: none;
  margin-left: 0.5rem;
  background-color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.lightButton : theme.colors.light};
  border: none;
  border-radius: 2rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.light || "#460073"}, ${({ theme }) => theme.colors.highlight2 || "#AF24FF"});
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.25s ease;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
    
    &::after {
      transform: scaleX(1);
    }
  }
  
  &:focus {
    outline: none;
  }
  
  &:active {
    border: none;
    transform: translateY(1px);
  }
`;

export const FilterPopup = styled.div`
  position: absolute;
  margin-top: 3rem;
  width: 24rem;
  background-color: ${({ theme }) => theme.colors.tableBackground || "#fff"};
  border: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.1)"};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 1.25rem;
  max-height: 80vh;
  overflow-y: hidden;
  margin-left: -18rem;
  z-index: 50;
  border-radius: 0.5rem;
  animation: slideIn 0.25s cubic-bezier(0.21, 0.61, 0.35, 1);
  backdrop-filter: blur(8px);
  
  @media (prefers-color-scheme: dark) {
    border-color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
    box-shadow: 0 10px 30px rgba(175, 36, 255, 0.1), 0 4px 10px rgba(0, 0, 0, 0.15);
    background-color: ${({ theme }) => `${theme.colors.tableBackground}f0` || "rgba(25,25,25,0.95)"};
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
      box-shadow: 0 0 0 rgba(0, 0, 0, 0);
    }
    to {
      opacity: 1;
      transform: translateY(0);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
    }
  }
`;

export const SearchBox = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.125)"};
  border-radius: 0.375rem;
  padding: 0 0.5rem;
  margin-bottom: 1rem;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
  outline: none;
  border: none;
  background: ${({ theme }) => theme.colors.searchbg || "transparent"};
  color: ${({ theme }) => theme.colors.textColor || "#000"};
`;

export const FilterGroup = styled.div`
  margin-bottom: 1rem;
`;

export const FilterGroupTitle = styled.h4`
  font-weight: 600;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textColor || "#000"};
  margin-bottom: 0.5rem;
`;

export const ApplyFilterButton = styled.button`
  margin-top: 0.5rem;
  background-color: ${({ theme }) => theme.colors.light || "#460073"};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border-radius: 2rem;
  border: none;
  box-shadow: ${({ theme }) => theme.shadows.boxShadow || "2px 2px 4.2px hsl(0deg 0% 0% / 0.17)"};
  cursor: pointer;
  transition: all 0.2s ease;
  height: 2.25rem;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const PolicyUpdateBox = styled.div`
  background-color: ${({ theme }) => theme.colors.tableBackground || "#fff"};
  border: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.15)"};
  border-radius: 0.5rem;
  padding: 1.25rem;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15), 0 5px 10px rgba(0, 0, 0, 0.12);
  min-width: 18rem;
  max-width: 24rem;
  width: auto;
  min-height: 11.125rem; /* Base height using golden ratio */
  max-height: 14.833rem; /* Max height while maintaining golden ratio */
  height: auto;
  transform: translateY(5px);
  display: flex;
  flex-direction: column;
  position: relative;
  
  /* Completely opaque background */
  background-color: ${({ theme }) => theme.colors.tableBackground || "#fff"};
  
  @media (prefers-color-scheme: dark) {
    border-color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.25), 0 5px 10px rgba(0, 0, 0, 0.22);
    background-color: ${({ theme }) => theme.colors.tableBackground || "#1c1c1c"};
  }
`;

export const PolicyUpdateTitle = styled.h4`
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
  letter-spacing: 0.02em;
  position: relative;
  padding-bottom: 0.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 2rem;
    height: 1px;
    background: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
    opacity: 0.5;
  }
`;

export const PolicyUpdateText = styled.p`
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  line-height: 1.6;
  flex: 1;
  color: ${({ theme }) => theme.colors.textColor || "#000"};
  padding-right: 0.5rem;
  max-height: 11rem; /* Allow content to define height up to this max */
  overflow-y: auto;
  letter-spacing: 0.01em;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
    border-radius: 3px;
    opacity: 0.3;
  }
`;

export const PolicyUpdateLink = styled.a`
  color: ${({ theme }) => theme.colors.highlight2 || "#AF24FF"};
  font-size: 0.875rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const StatsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const StatCard = styled.div`
  flex: 1;
  min-width: 150px;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.tableBackground || "#fff"};
  border: 1px solid ${({ theme }) => theme.borders.borderColor || "rgba(0,0,0,.125)"};
  border-radius: 0.5rem;
  box-shadow: ${({ theme }) => theme.shadows.boxShadow || "2px 2px 4.2px hsl(0deg 0% 0% / 0.17)"};
  text-align: center;
`;

export const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

export const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textDisabledColor || "#96968C"};
`;

export const CircleArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  box-shadow: ${({ theme }) => theme.shadows.boxShadow || "2px 2px 4.2px hsl(0deg 0% 0% / 0.17)"};
  cursor: pointer;
  background-color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.lightButton: theme.colors.light};
  
  svg {
    width: 16px;
    height: 16px;
    stroke: #FFFFFF;
  }
`;

export const InfoIconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-right: 8px;
  flex-shrink: 0;

  &:hover > div {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }
`;

export const InfoTooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  z-index: 1000; /* High z-index to ensure it's above everything */
  top: 20px;
  left: 0;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  border: none;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transform: translateY(8px);

  &:hover {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }
`;